import React, { useRef, useState } from "react";
import FieldError from "../FieldError";
import FieldLabel from "../FieldLabel";
import { useLanguage } from "../../../context/LanguageContext";
import uploadIcon from "../../../assets/images/upload-icon.svg";

/** Must match backend multer allow-list (JPEG / PNG / WEBP / PDF). */
const ACCEPTED_FILES =
  "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,.pdf,application/pdf";
const ALLOWED_EXTENSIONS = /\.(jpe?g|png|webp|pdf)$/i;
const ALLOWED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);

const isAllowedUpload = (file) => {
  const type = (file.type || "").toLowerCase();
  const name = file.name || "";

  if (ALLOWED_MIME.has(type)) return true;
  if (type.startsWith("image/") && !ALLOWED_MIME.has(type)) return false;
  return ALLOWED_EXTENSIONS.test(name);
};

const FileField = ({
  id,
  label,
  required = true,
  file,
  onChange,
  infoContent,
  infoLabel,
  error = "",
  accept = ACCEPTED_FILES,
}) => {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [typeError, setTypeError] = useState("");
  const displayError = typeError ? t(typeError) : error;

  const handleChange = (event) => {
    const nextFile = event.target.files?.[0];

    if (!nextFile) {
      setTypeError("");
      onChange(event);
      return;
    }

    if (!isAllowedUpload(nextFile)) {
      event.target.value = "";
      setTypeError("signup.errors.fileType");
      return;
    }

    setTypeError("");
    onChange(event);
  };

  return (
    <div className="signup-form__field">
      <FieldLabel
        htmlFor={id}
        required={required}
        infoContent={infoContent}
        infoLabel={infoLabel}
      >
        {label}
      </FieldLabel>
      <button
        type="button"
        className={`signup-form__upload${displayError ? " is-invalid" : ""}`}
        onClick={() => inputRef.current?.click()}
      >
        <img src={uploadIcon} alt="" width={24} height={24} aria-hidden="true" />
        <span>{file ? file.name : t("signup.chooseFile")}</span>
      </button>
      <input
        ref={inputRef}
        id={id}
        className="signup-form__file-input"
        type="file"
        accept={accept}
        onChange={handleChange}
        required={required && !file}
        tabIndex={-1}
        aria-hidden="true"
      />
      <FieldError id={`${id}-error`}>{displayError}</FieldError>
    </div>
  );
};

export default FileField;
